import { Component, OnInit, OnDestroy, inject, signal, computed, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  Database,
  ref,
  objectVal,
  set,
  update,
  onDisconnect,
  serverTimestamp as rtDbServerTimestamp,
} from '@angular/fire/database';
import { NGXLogger } from 'ngx-logger';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { QuizService } from '../../../services/quiz.service';
import { UtilsService } from '../../../services/utils.service';
import { AppState } from '../../../state/appstate';
import { UserModel } from '../../../models/user.model';
import { Quiz, QuizStatus } from '../../../models/quiz.model';
import { QuizQuestion, QuestionType } from '../../../models/question.quiz.model';
import { QuizSession } from '../../../models/session.quiz.model';
import { trigger, transition, style, animate } from '@angular/animations';
import { AsyncPipe } from '@angular/common';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';
import { BtnActionComponent } from '../../components/buttons/btn-action/btn-action.component';

@Component({
  selector: 'app-live-participant-quiz',
  standalone: true,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    BtnBackComponent,
    BtnActionComponent,
  ],
  templateUrl: './live-participant-quiz.component.html',
  styles: [],
  animations: [
    trigger('fade', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('200ms ease-in', style({ opacity: 0 }))]),
    ]),
    trigger('slideInOut', [
      transition(
        ':enter',
        [
          style({ transform: '{{startPos}}' }),
          animate(
            '300ms cubic-bezier(0.25, 0.8, 0.25, 1)',
            style({ transform: 'translate(0,0)' }),
          ),
        ],
        { params: { startPos: 'translateX(100%)' } },
      ),
      transition(
        ':leave',
        [animate('250ms ease-in', style({ transform: '{{startPos}}' }))],
        { params: { startPos: 'translateX(100%)' } },
      ),
    ]),
  ],
})
export class LiveParticipantQuizComponent implements OnInit, OnDestroy {
  store: Store<AppState> = inject(Store<AppState>);
  router = inject(Router);
  private db: Database = inject(Database);
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private logger: NGXLogger = inject(NGXLogger);
  private quizService = inject(QuizService);
  private utilsService = inject(UtilsService);

  groupId: string | null = this.activatedRoute.snapshot.paramMap.get('id');
  quizId: string | null = this.activatedRoute.snapshot.paramMap.get('quizId');

  user: UserModel | null = null;
  quiz: Quiz | null = null;

  remainingTime = signal(0);
  private timerInterval: any;

  private sessionDbRef = ref(
    this.db,
    `sessions/${this.groupId}/${this.quizId}`
  );

  session = toSignal(
    objectVal<QuizSession>(this.sessionDbRef).pipe(
      tap((session) => {
        if (session && session.currentIndex >= 0 && this.quiz) {
          const questionId = this.quiz.questions
            ? this.quiz.questions[session.currentIndex]
            : null;
          if (questionId) {
            this.getQuestionById(questionId);
            if (this.user) {
              this.trackResponse(questionId);
            }
            if (session.startTime) {
              this.syncTimer(session.startTime, session.durationPerQuestion || 30);
            } else {
              this.remainingTime.set(this.quiz.answerDuration || 30);
            }
          }
        }
      })
    ),
    { initialValue: null }
  );

  selectedQuestions = signal<QuizQuestion[]>([]);
  currentQuestion = computed(() => {
    const s = this.session();
    const allLoadedQuestions = this.selectedQuestions();

    if (!s || s.currentIndex < 0 || !this.quiz?.questions) {
      return null;
    }

    const activeId = this.quiz.questions[s.currentIndex];
    const found = allLoadedQuestions.find((q) => q.id === activeId);

    if (!found) {
      this.getQuestionById(activeId);
      return null;
    }

    return found;
  });

  isSubmitted = signal(false);
  totalScore = signal(0);

  selectedOptionId = signal<string | null>(null);
  selectedOptionText = signal<string | null>(null);
  typedAnswer = signal<string>('');

  private destroy$ = new Subject<void>();
  private responseSubscription: Subscription | null = null;

  quizStatus = QuizStatus;
  questionTypes = {
    multipleChoice: 'multipleChoice',
    fillInTheBlank: 'fillInTheBlank',
    trueFalse: 'trueFalse',
    shortAnswer: 'shortAnswer',
  };

  ngOnInit(): void {
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.user = user;
        if (this.user) {
          this.initParticipant();
        }
      });
    this.getQuiz();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    if (this.responseSubscription) {
      this.responseSubscription.unsubscribe();
    }

    if (this.user && this.groupId && this.quizId) {
      const presenceRef = ref(
        this.db,
        `presence/${this.groupId}/${this.quizId}/${this.user.uid}`
      );
      update(presenceRef, {
        status: 'offline',
        lastSeen: rtDbServerTimestamp(),
      });
    }
  }

  initParticipant() {
    if (!this.groupId || !this.quizId || !this.user) return;

    const presenceRef = ref(
      this.db,
      `presence/${this.groupId}/${this.quizId}/${this.user.uid}`
    );

    // Register presence
    update(presenceRef, {
      name: this.user.name || 'Anonymous',
      photoURL: this.user.photoURL || '',
      status: 'online',
      lastSeen: rtDbServerTimestamp(),
    });

    // Handle offline triggers on socket close
    onDisconnect(presenceRef).update({
      status: 'offline',
      lastSeen: rtDbServerTimestamp(),
    });

    // Listen to cumulative score changes
    objectVal<any>(presenceRef)
      .pipe(takeUntil(this.destroy$))
      .subscribe((presence) => {
        if (presence) {
          this.totalScore.set(presence.userScore ?? presence.totalScore ?? 0);
        }
      });

    // Handle edge case where session is already initialized before user profile loaded
    const s = this.session();
    if (s && s.currentIndex >= 0 && this.quiz?.questions) {
      const questionId = this.quiz.questions[s.currentIndex];
      if (questionId) {
        this.trackResponse(questionId);
      }
    }
  }

  getQuiz() {
    if (!this.groupId || !this.quizId) return;

    this.quizService
      .getQuizById({ groupId: this.groupId, quizId: this.quizId })
      .subscribe({
        next: (quiz) => {
          this.quiz = quiz;
          if (this.quiz == null) {
            this.logger.error(
              `Quiz with ID ${this.quizId} not found in group ${this.groupId}`
            );
            this.router.navigate(['/school', this.groupId, 'quizzes']);
          } else {
            const s = this.session();
            if (s && s.currentIndex >= 0 && this.quiz.questions) {
              const currentId = this.quiz.questions[s.currentIndex];
              if (currentId) {
                this.getQuestionById(currentId);
                if (this.user) {
                  this.trackResponse(currentId);
                }
              }
            } else if ((this.quiz.questions ?? [])?.length > 0) {
              this.getQuestionById(this.quiz.questions![0]);
              this.remainingTime.set(this.quiz.answerDuration || 30);
            }
          }
        },
        error: (error) => {
          this.logger.error('Error fetching quiz: ', error);
        },
      });
  }

  getQuestionById(questionId: string) {
    const alreadyLoaded = this.selectedQuestions().some((q) => q.id === questionId);
    if (alreadyLoaded) return;

    this.quizService.getQuestionById(this.groupId!, questionId).subscribe({
      next: (question) => {
        if (question) {
          this.selectedQuestions.update((current) => {
            const index = current.findIndex((q) => q.id === questionId);
            if (index !== -1) {
              const updated = [...current];
              updated[index] = question;
              return updated;
            } else {
              return [...current, question];
            }
          });
        }
      },
    });
  }

  trackResponse(questionId: string) {
    if (this.responseSubscription) {
      this.responseSubscription.unsubscribe();
    }

    const responsePath = ref(
      this.db,
      `responses/${this.groupId}/${this.quizId}/${questionId}/${this.user?.uid}`
    );

    this.responseSubscription = objectVal<any>(responsePath).subscribe((val) => {
      this.isSubmitted.set(!!val);
    });
  }

  syncTimer(startTime: number, duration: number) {
    if (this.timerInterval) clearInterval(this.timerInterval);

    const updateTimer = () => {
      const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
      const timeLeft = Math.max(0, duration - elapsedSeconds);

      this.remainingTime.set(timeLeft);

      if (timeLeft <= 0) {
        if (this.timerInterval) clearInterval(this.timerInterval);
        if (!this.isSubmitted()) {
          this.submitAnswer(true); // Automatically submit on timeout
        }
      }
    };

    updateTimer();

    if (this.remainingTime() > 0) {
      this.timerInterval = setInterval(updateTimer, 1000);
    }
  }

  selectOption(optionId: string, optionText: string) {
    this.selectedOptionId.set(optionId);
    this.selectedOptionText.set(optionText);
  }

  selectTrueFalse(value: string) {
    this.selectedOptionText.set(value);
  }

  onTypedAnswerChange(event: Event) {
    const target = event.target as HTMLTextAreaElement;
    this.typedAnswer.set(target.value);
  }

  async submitAnswer(isTimeout = false, isSkipped = false) {
    const q = this.currentQuestion();
    if (!q || !this.user || this.isSubmitted()) return;

    let isCorrect: boolean | null = false;
    let score = 0;
    let finalAnswer = '';

    if (isTimeout) {
      finalAnswer = '[TIMED OUT]';
      isCorrect = false;
      score = 0;
    } else if (isSkipped) {
      finalAnswer = '[SKIPPED]';
      isCorrect = false;
      score = 0;
    } else {
      if (q.questionType === this.questionTypes.multipleChoice) {
        finalAnswer = this.selectedOptionText() || '';
        isCorrect = this.selectedOptionId() === q.correctOptionId;
      } else if (q.questionType === this.questionTypes.trueFalse) {
        finalAnswer = this.selectedOptionText() || '';
        isCorrect = finalAnswer.trim().toLowerCase() === q.simpleAnswer?.trim().toLowerCase();
      } else if (q.questionType === this.questionTypes.shortAnswer) {
        finalAnswer = this.typedAnswer();
        isCorrect = null; // host grades manually
      } else { // fillInTheBlank
        finalAnswer = this.typedAnswer();
        isCorrect = finalAnswer.trim().toLowerCase() === q.simpleAnswer?.trim().toLowerCase();
      }
      score = isCorrect ? (q.score || 0) : 0;
    }

    const duration = this.session()?.durationPerQuestion || 30;
    const timeTaken = isTimeout ? duration : Math.max(0, duration - this.remainingTime());

    const responseData = {
      studentId: this.user.uid,
      studentName: this.user.name || 'Anonymous',
      studentAnswer: finalAnswer,
      timeTaken: timeTaken,
      score: score,
      isCorrect: isCorrect,
      answerStatus: isTimeout ? 'timeout' : (isSkipped ? 'skipped' : 'answered'),
      timestamp: rtDbServerTimestamp(),
    };

    try {
      const responseRef = ref(
        this.db,
        `responses/${this.groupId}/${this.quizId}/${q.id}/${this.user.uid}`
      );
      await set(responseRef, responseData);

      const presenceRef = ref(
        this.db,
        `presence/${this.groupId}/${this.quizId}/${this.user.uid}`
      );
      const newTotalScore = this.totalScore() + score;
      await update(presenceRef, {
        userScore: newTotalScore,
        totalScore: newTotalScore,
        lastAnsweredIndex: this.session()?.currentIndex ?? 0,
        lastSeen: rtDbServerTimestamp(),
      });

      // Clear input fields locally
      this.selectedOptionId.set(null);
      this.selectedOptionText.set(null);
      this.typedAnswer.set('');
    } catch (err) {
      this.logger.error('Error submitting answer:', err);
    }
  }

  async exitQuiz() {
    if (this.user && this.groupId && this.quizId) {
      const presenceRef = ref(
        this.db,
        `presence/${this.groupId}/${this.quizId}/${this.user.uid}`
      );
      await update(presenceRef, {
        status: 'offline',
        lastSeen: rtDbServerTimestamp(),
      });
    }
    this.router.navigate(['/school', this.groupId, 'quizzes']);
  }
}
